import { useEffect, useMemo, useState } from 'react';
import { Icon, IconButton, LinearProgress, TablePagination, Paper, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TableRow, AlertColor } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { CampanhasService } from 'shared/services/campanhas/campanhasService';
import { CanaisService } from 'shared/services/canais/canaisService';
import { IListagemCampanha } from 'shared/types';
import { FerramentasDaListagem, IListaFiltros, AlertaSnackBar } from 'shared/components';
import { LayoutBaseDePagina } from 'shared/layouts';
import { currentDateHour, IKeyValue, listagemCanalToIKeyValue, listagemStatusToIKeyValue, listagemUsersToIKeyValue, stringToDate } from 'shared/utils';

import { useSelectedOrganization } from 'shared/store/organization';

export const ListagemDeCampanhas: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const navigate = useNavigate();
  const { t } = useTranslation();

  const [rows, setRows] = useState<IListagemCampanha[]>([]);

  const [listKeyValueCanal, setListKeyValueCanal] = useState<IKeyValue[]>([]);
  const [listKeyValueStatus, setListKeyValueStatus] = useState<IKeyValue[]>([]);
  const [listKeyValueUsers, setListKeyValueUsers] = useState<IKeyValue[]>([]);

  const [stateSnack, setStateSnack] = useState({
    open: false,
    severity: 'success',
    message: '',
  });

  const listKeyValueEnvio: IKeyValue[] = [
    {
      key: 'M',
      value: 'Manual'
    }, {
      key: 'O',
      value: 'Online'
    }];

  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingCanal, setIsLoadingCanal] = useState(true);
  const [isLoadingStatus, setIsLoadingStatus] = useState(true);
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  const [totalCount, setTotalCount] = useState(0);

  const [eventoPesquisar, setEventoPesquisar] = useState(false);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const org = useSelectedOrganization();

  const buscaCanal = useMemo(() => {
    return searchParams.get('campanhaCanal') || [];
  }, [searchParams]);

  const buscaStatus = useMemo(() => {
    return searchParams.get('stCampanha') || '';
  }, [searchParams]);

  const buscaCodigo = useMemo(() => {
    return searchParams.get('cdCampanha') || '';
  }, [searchParams]);

  const buscaEnvio = useMemo(() => {
    return searchParams.get('tpEnvio') || '';
  }, [searchParams]);

  const buscaDtInicio = useMemo(() => {
    return searchParams.get('dtInicio') || currentDateHour('primeira');
  }, [searchParams]);

  const buscaDtFim = useMemo(() => {
    return searchParams.get('dtFim') || currentDateHour('ultima');
  }, [searchParams]);

  const buscaNmCampanha = useMemo(() => {
    return searchParams.get('nmCampanha') || '';
  }, [searchParams]);

  const buscaUsrCampanha = useMemo(() => {
    return searchParams.get('usrCampanha') || '';
  }, [searchParams]);


  useEffect(() => {
    setIsLoadingCanal(true);
    setIsLoadingStatus(true);
    setIsLoadingUsers(true);
    CanaisService.getByOrg(Number(org?.CodigoOrganizacao))
      .then((result) => {
        setIsLoadingCanal(false);
        if (result instanceof Error) {
          setStateSnack({open:true, severity:'error',message:`Ocorreu o seguinte erro: ${result.message}`});
        } else {
          setListKeyValueCanal(listagemCanalToIKeyValue(result));
        }
      });
    CampanhasService.getStatus(Number(org?.CodigoOrganizacao))
      .then((result) => {
        setIsLoadingStatus(false);
        if (result instanceof Error) {
          setStateSnack({open:true, severity:'error',message:`Ocorreu o seguinte erro: ${result.message}`});
        } else {
          setListKeyValueStatus(listagemStatusToIKeyValue(result));
        }
      });
    CampanhasService.getUsers(Number(org?.CodigoOrganizacao))
      .then((result) => {
        setIsLoadingUsers(false);
        if (result instanceof Error) {
          setStateSnack({open:true, severity:'error',message:`Ocorreu o seguinte erro: ${result.message}`});
        } else {
          setListKeyValueUsers(listagemUsersToIKeyValue(result));
        }
      });

  }, []);


  const listaFiltros: IListaFiltros[] = [{
    tipo: 'SelectMultiple',
    mapValue: listKeyValueCanal.map((itemk) => itemk),
    label: t('pages.listagemDeCampanhas.canal'),
    textoBusca: typeof buscaCanal === 'string' ? buscaCanal.split(',') : buscaCanal,
    handleTextoBusca: texto => { searchParams.set('campanhaCanal', texto.toString()); setSearchParams(searchParams, { replace: true }); }
  },
  {
    tipo: 'Select',
    mapValue: listKeyValueStatus.map((itemk) => itemk),
    label: t('pages.listagemDeCampanhas.status'),
    textoBusca: buscaStatus,
    handleTextoBusca: texto => { searchParams.set('stCampanha', texto.toString()); setSearchParams(searchParams, { replace: true }); }
  },
  {
    tipo: 'TextFieldNumeric',
    mapValue: [],
    label: t('pages.listagemDeCampanhas.codigoCampanha'),
    textoBusca: buscaCodigo,
    handleTextoBusca: texto => { searchParams.set('cdCampanha', texto.toString()); setSearchParams(searchParams, { replace: true }); }
  },
  {
    tipo: 'TextField',
    mapValue: [],
    label: t('pages.listagemDeCampanhas.nomeCampanha'),
    textoBusca: buscaNmCampanha,
    handleTextoBusca: texto => { searchParams.set('nmCampanha', texto.toString()); setSearchParams(searchParams, { replace: true }); }
  },
  {
    tipo: 'Select',
    mapValue: listKeyValueEnvio,
    label: t('pages.listagemDeCampanhas.tipoEnvio'),
    textoBusca: buscaEnvio,
    handleTextoBusca: texto => { searchParams.set('tpEnvio', texto.toString()); setSearchParams(searchParams, { replace: true }); }
  },
  {
    tipo: 'DateTimePicker',
    mapValue: [],
    label: t('pages.listagemDeCampanhas.dataInicio'),
    textoBusca: buscaDtInicio,
    handleTextoBusca: texto => { searchParams.set('dtInicio', texto.toString()); setSearchParams(searchParams, { replace: true }); }
  },
  {
    tipo: 'DateTimePicker',
    mapValue: [],
    label: t('pages.listagemDeCampanhas.dataTermino'),
    textoBusca: buscaDtFim,
    handleTextoBusca: texto => { searchParams.set('dtFim', texto.toString()); setSearchParams(searchParams, { replace: true }); }
  },
  {
    tipo: 'Select',
    mapValue: listKeyValueUsers.map((itemk) => itemk),
    label: t('pages.listagemDeCampanhas.usuarioResponsavel'),
    textoBusca: buscaUsrCampanha,
    handleTextoBusca: texto => { searchParams.set('usrCampanha', texto.toString()); setSearchParams(searchParams, { replace: true }); }
  },
  ];

  useEffect(() => {
    setIsLoading(true);
    const filtro = `?campanhaCanal=${buscaCanal}&stCampanha=${buscaStatus}&cdCampanha=${buscaCodigo}&tpEnvio=${buscaEnvio}&dtInicio=${buscaDtInicio}&dtFim=${buscaDtFim}&nmCampanha=${buscaNmCampanha}&usrCampanha=${buscaUsrCampanha}`;
    CampanhasService.getAll(Number(org?.CodigoOrganizacao), filtro)
      .then((result) => {
        setIsLoading(false);

        if (result instanceof Error) {
          setStateSnack({open:true, severity:'error',message:`Ocorreu o seguinte erro: ${result.message}`});
        } else {
          setTotalCount(result.length);
          setRows(result);
          setPage(0);
        }
      });

  }, [eventoPesquisar]);

  const handleLimpar = () => {
    // eslint-disable-next-line prefer-const
    let parametros: string[] = [];
    searchParams.forEach(function (value, key) {
      parametros.push(key);
    });
    parametros.map((item) => (
      searchParams.delete(item)
    ));
    setSearchParams(searchParams, { replace: true });
    setEventoPesquisar(!eventoPesquisar);
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCloseSnack = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }

    setStateSnack({...stateSnack, open:false});
  };
  
  return (
    <>
      <LayoutBaseDePagina
        titulo={t('pages.listagemDeCampanhas.campanhas')}
        drawerDeFiltros={!isLoadingCanal && !isLoadingStatus && !isLoadingUsers && (
          <FerramentasDaListagem
            listaFiltros={listaFiltros}
            handlePesquisar={() => setEventoPesquisar(!eventoPesquisar)}
            handleLimpar={() => handleLimpar()}

          />
        )}
      >
        <TableContainer component={Paper} variant="outlined" sx={{ m: 1, width: 'auto' }}>
          <Table stickyHeader >
            <TableHead>
              <TableRow>
                <TableCell width={60}>{t('global.acoes')}</TableCell>
                <TableCell>{t('pages.listagemDeCampanhas.codigoCampanha')}</TableCell>
                <TableCell>{t('pages.listagemDeCampanhas.nomeCampanha')}</TableCell>
                <TableCell>{t('pages.listagemDeCampanhas.dataInicio')}</TableCell>
                <TableCell>{t('pages.listagemDeCampanhas.dataTermino')}</TableCell>
                <TableCell>{t('pages.listagemDeCampanhas.tipoEnvio')}</TableCell>
                <TableCell>{t('pages.listagemDeCampanhas.status')}</TableCell>
                <TableCell>{t('pages.listagemDeCampanhas.disparo')}</TableCell>
                <TableCell>{t('pages.listagemDeCampanhas.canal')}</TableCell>
                <TableCell>{t('pages.listagemDeCampanhas.online')}</TableCell>
              </TableRow>
            </TableHead>
            {!isLoading && (<TableBody>
              {rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => (
                <TableRow key={row.IdCampanha}>
                  <TableCell>
                    <IconButton size="small" disabled={true}>
                      <Icon>edit</Icon>
                    </IconButton>
                  </TableCell>
                  <TableCell>{row.CodigoCampanha}</TableCell>
                  <TableCell>{row.NomeCampanha}</TableCell>
                  <TableCell>{stringToDate(row.DataCampanhaInicio)}</TableCell>
                  <TableCell>{stringToDate(row.DataCampanhaTermino)}</TableCell>
                  <TableCell>{row.TipoCampanha}</TableCell>
                  <TableCell>{row.StatusCampanha}</TableCell>
                  <TableCell>{row.ConfiguracaoDisparo}</TableCell>
                  <TableCell>{row.ListaCanal}</TableCell>
                  <TableCell>{row.FlagCampanhaOnline}</TableCell>
                </TableRow>
              ))}
            </TableBody>)}

            {totalCount === 0 && !isLoading && (
              <caption>{t('global.listaVazia')}</caption>
            )}

            <TableFooter>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={10}>
                    <LinearProgress variant='indeterminate' />
                  </TableCell>
                </TableRow>
              )}
              {(!isLoading && totalCount > 0 && totalCount > 10) && (
                <TableRow>
                  <TableCell colSpan={10}>
                    <TablePagination
                      component="div"
                      labelDisplayedRows={({ from, to, count }) => `${from} - ${to} ${t('global.of')} ${count !== -1 ? count : `${t('global.moreThan')} ${to}`}`}
                      labelRowsPerPage={t('global.rowsPerPage')}
                      showFirstButton={true}
                      showLastButton={true}
                      page={page}
                      count={rows.length}
                      rowsPerPage={rowsPerPage}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                    //onChange={(_, newPage) => setSearchParams({ busca, pagina: newPage.toString() }, { replace: true })}
                    />
                  </TableCell>
                </TableRow>
              )}
            </TableFooter>
          </Table>
        </TableContainer>

      </LayoutBaseDePagina>
      <AlertaSnackBar 
        open={stateSnack.open} 
        severity={stateSnack.severity as AlertColor} 
        message={stateSnack.message} 
        handleClose={handleCloseSnack} 
      />
    </>
  );
};
